import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { accreditationAPI } from '../api/client';
import Breadcrumb from '../components/Breadcrumb';
import { Award, CheckCircle, AlertCircle, XCircle, Upload } from 'lucide-react';

const Accreditation = () => {
  const { orgId } = useParams();
  const { currentOrg } = useAuth();
  const [activeTab, setActiveTab] = useState('criteria');
  const [criteria, setCriteria] = useState([]);
  const [evidenceMap, setEvidenceMap] = useState({});
  const [actionPlans, setActionPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCriterion, setSelectedCriterion] = useState(null);

  useEffect(() => {
    if (orgId || currentOrg) {
      loadData();
    }
  }, [orgId, currentOrg]);

  const loadData = async () => {
    try {
      const id = orgId || currentOrg?.id;
      if (!id) return;

      // Load criteria
      const criteriaResponse = await accreditationAPI.listCriteria();
      setCriteria(criteriaResponse.data);

      // Load evidence for this organization
      const evidenceResponse = await accreditationAPI.listEvidence(id);
      const evidenceList = evidenceResponse.data;

      // Create a map of criterionId -> evidence
      const map = {};
      evidenceList.forEach((evidence) => {
        map[evidence.criterionId] = evidence;
      });
      setEvidenceMap(map);

      // Load action plans
      const actionResponse = await accreditationAPI.listActionPlans(id);
      setActionPlans(actionResponse.data);
    } catch (error) {
      console.error('Veri yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get criterion status and evidence count
  const getCriterionStatus = (criterion) => {
    const evidence = evidenceMap[criterion.id];
    return {
      status: evidence?.status || 'Yok',
      evidenceCount: evidence ? 1 : 0,
      evidence: evidence,
    };
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'Tamam':
        return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' };
      case 'Kısmen Karşılanıyor':
        return { icon: AlertCircle, color: 'text-yellow-600', bg: 'bg-yellow-100' };
      case 'Geliştirilmeli':
        return { icon: AlertCircle, color: 'text-orange-600', bg: 'bg-orange-100' };
      case 'Yok':
        return { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' };
      default:
        return { icon: AlertCircle, color: 'text-gray-600', bg: 'bg-gray-100' };
    }
  };

  const completedCount = criteria.filter(
    (c) => getCriterionStatus(c).status === 'Tamam'
  ).length;
  const progressPercentage = criteria.length > 0 ? Math.round((completedCount / criteria.length) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb 
        items={[
          { label: 'Okullar', path: '/organizations' },
          { label: currentOrg?.name || 'Okul', path: null },
          { label: 'Akreditasyon', path: null }
        ]} 
      />
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Akreditasyon Yönetimi</h1>
        <p className="text-gray-600">İlkadım360 standartları ve ilerleme takibi</p>
      </div>

      {/* Progress Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">İlkadım360 Standartları v2.0</h2>
            <p className="text-sm text-gray-600">Toplam {criteria.length} kriter</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-blue-600">{progressPercentage}%</p>
            <p className="text-sm text-gray-600">Tamamlandı</p>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-blue-600 h-3 rounded-full transition-all"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
        <div className="mt-4 flex gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-gray-600">
              Tamam: {criteria.filter((c) => getCriterionStatus(c).status === 'Tamam').length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
            <span className="text-gray-600">
              Geliştirilmeli: {criteria.filter((c) => getCriterionStatus(c).status === 'Geliştirilmeli').length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span className="text-gray-600">
              Yok: {criteria.filter((c) => getCriterionStatus(c).status === 'Yok').length}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('criteria')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'criteria'
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Kriterler ve Kanıtlar
          </button>
          <button
            onClick={() => setActiveTab('actions')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'actions'
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Aksiyon Planı ({actionPlans.length})
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'criteria' ? (
        <>
      {/* Criteria List */}
      <div className="space-y-4">
        {[...new Set(criteria.map((c) => c.category))].map((category) => (
          <div key={category} className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{category}</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {criteria
                .filter((c) => c.category === category)
                .map((criterion) => {
                  const criterionStatus = getCriterionStatus(criterion);
                  const statusInfo = getStatusInfo(criterionStatus.status);
                  const StatusIcon = statusInfo.icon;
                  return (
                    <div key={criterion.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded">
                              {criterion.code}
                            </span>
                            <h4 className="text-lg font-medium text-gray-900">{criterion.title}</h4>
                          </div>
                          <p className="text-gray-600 mb-3">{criterion.description}</p>
                          <div className="flex items-center gap-4 text-sm">
                            <div className={`flex items-center gap-2 px-3 py-1 ${statusInfo.bg} ${statusInfo.color} rounded-full`}>
                              <StatusIcon size={16} />
                              <span className="font-medium">{criterionStatus.status}</span>
                            </div>
                            <span className="text-gray-600">
                              {criterionStatus.evidenceCount} kanıt
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedCriterion({ ...criterion, evidence: criterionStatus.evidence })}
                          className="ml-4 flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                        >
                          <Upload size={16} />
                          {criterionStatus.evidence ? 'Kanıt Güncelle' : 'Kanıt Ekle'}
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        ))}
      </div>
      </>
      ) : (
        <ActionPlansTab 
          orgId={orgId || currentOrg?.id} 
          actions={actionPlans} 
          criteria={criteria}
          onUpdate={loadData} 
        />
      )}

      {/* Evidence Modal */}
      {selectedCriterion && (
        <EvidenceModal
          orgId={orgId || currentOrg?.id}
          criterion={selectedCriterion}
          onClose={() => setSelectedCriterion(null)}
          onSuccess={() => {
            setSelectedCriterion(null);
            loadData();
          }}
        />
      )}
    </div>
  );
};


const EvidenceModal = ({ orgId, criterion, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    status: criterion.evidence?.status || 'Yok',
    description: criterion.evidence?.description || '',
    attachments: criterion.evidence?.attachments || [],
  });
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // For MVP, we'll store file names only (in production, upload to cloud storage)
    const fileNames = files.map((file) => file.name);
    setUploadedFiles(files);
    setFormData({
      ...formData,
      attachments: [...formData.attachments, ...fileNames],
    });
  };

  const removeAttachment = (index) => {
    const newAttachments = formData.attachments.filter((_, i) => i !== index);
    setFormData({ ...formData, attachments: newAttachments });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const evidenceData = {
        criterionId: criterion.id,
        status: formData.status,
        description: formData.description,
        attachments: formData.attachments,
      };

      if (criterion.evidence) {
        // Update existing evidence
        await accreditationAPI.updateEvidence(orgId, criterion.evidence.id, evidenceData);
      } else {
        // Create new evidence
        await accreditationAPI.createEvidence(orgId, evidenceData);
      }

      onSuccess();
    } catch (error) {
      console.error('Kanıt kaydedilemedi:', error);
      alert('Kanıt kaydedilemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {criterion.evidence ? 'Kanıt Güncelle' : 'Kanıt Ekle'} - {criterion.code}
          </h3>
          <p className="text-gray-600">{criterion.title}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Durum *
            </label>
            <select
              required
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="Yok">Yok</option>
              <option value="Geliştirilmeli">Geliştirilmeli</option>
              <option value="Kısmen Karşılanıyor">Kısmen Karşılanıyor</option>
              <option value="Tamam">Tamam</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Açıklama
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Bu kritere ilişkin açıklamanızı ve aldığınız aksiyonları yazın..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dosya Yükle (Fotoğraf, Belge)
            </label>
            <input
              type="file"
              id="file-upload"
              multiple
              accept="image/*,.pdf,.doc,.docx"
              onChange={handleFileUpload}
              className="hidden"
            />
            <label
              htmlFor="file-upload"
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors block"
            >
              <Upload className="mx-auto text-gray-400 mb-2" size={32} />
              <p className="text-sm text-gray-600">
                Fotoğraf veya belge yüklemek için tıklayın
              </p>
              <p className="text-xs text-gray-500 mt-1">
                PNG, JPG, PDF, DOC (Max 10MB)
              </p>
            </label>

            {/* Uploaded Files List */}
            {formData.attachments.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-gray-700">Yüklenen Dosyalar:</p>
                {formData.attachments.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg"
                  >
                    <span className="text-sm text-gray-700">{file}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <XCircle size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Kaydediliyor...' : criterion.evidence ? 'Güncelle' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ActionPlansTab = ({ orgId, actions, criteria, onUpdate }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-600">Akreditasyon hedefleri için aksiyon planları oluşturun ve takip edin</p>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + Yeni Aksiyon
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {actions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-600">Henüz aksiyon planı oluşturulmamış</p>
          </div>
        ) : (
          actions.map((action) => (
            <div key={action.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{action.title}</h3>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      action.priority === 'High' ? 'bg-red-100 text-red-700' :
                      action.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {action.priority}
                    </span>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      action.status === 'Completed' ? 'bg-green-100 text-green-700' :
                      action.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {action.status}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-3">{action.description}</p>
                  <p className="text-sm text-gray-500">
                    Hedef: {new Date(action.targetDate).toLocaleDateString('tr-TR')}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <ActionPlanModal
          orgId={orgId}
          criteria={criteria}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            onUpdate();
          }}
        />
      )}
    </div>
  );
};

const ActionPlanModal = ({ orgId, criteria, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    criterionId: criteria[0]?.id || '',
    title: '',
    description: '',
    targetDate: '',
    priority: 'Medium',
    status: 'Not Started',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await accreditationAPI.createActionPlan(orgId, {
        ...formData,
        targetDate: new Date(formData.targetDate).toISOString(),
      });
      onSuccess();
    } catch (error) {
      console.error('Aksiyon oluşturulamadı:', error);
      alert('Aksiyon oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Yeni Aksiyon Planı</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">İlgili Kriter *</label>
            <select
              required
              value={formData.criterionId}
              onChange={(e) => setFormData({ ...formData, criterionId: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {criteria.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} - {c.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Aksiyon Başlığı *</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Açıklama *</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hedef Tarih *</label>
              <input
                type="date"
                required
                value={formData.targetDate}
                onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Öncelik *</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="Low">Düşük</option>
                <option value="Medium">Orta</option>
                <option value="High">Yüksek</option>
              </select>
            </div>
          </div>

          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Oluşturuluyor...' : 'Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Accreditation;
